import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  constructor() { }

  public getCurrentPosition(options?: PositionOptions): Observable<GeolocationPosition> {
    const geolocation = navigator.geolocation

    if (!geolocation) {
      return throwError(() => new Error('Geolocation no está soportada'))
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true, // Fuerza GPS cuando sea posible
      timeout: 10000, // 10 segundos máximo
      maximumAge: 0 // No usar caché (siempre obtener nueva)
    }

    const finalOptions = { ...defaultOptions, ...options }

    return new Observable<GeolocationPosition>(observer => {
      geolocation.getCurrentPosition(
        (position) => {
          observer.next(position)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        },
        finalOptions
      )
    }).pipe(
      timeout(finalOptions.timeout!),
      catchError((e) => this.handleError(e))
    )
  }

  // Método para obtener múltiples lecturas y usar la más precisa
  public getAccuratePosition(attempts: number = 5): Observable<GeolocationPosition> {
    const positions: GeolocationPosition[] = []

    return new Observable<GeolocationPosition>(observer => {
      let completed = 0

      for (let i = 0; i < attempts; i++) {
        this.getCurrentPosition().subscribe({
          next: (position) => {
            positions.push(position)
            completed++

            if (completed === attempts) {
              const mostAccurate = this.getMostAccurate(positions)
              observer.next(mostAccurate)
              observer.complete()
            }
          },
          error: (error) => {
            completed++

            if (completed === attempts) {
              if (positions.length > 0) {
                const mostAccurate = this.getMostAccurate(positions)
                observer.next(mostAccurate)
                observer.complete()
              } else {
                observer.error(error)
              }
            }
          }
        })
      }
    })
  }

  private getMostAccurate(positions: GeolocationPosition[]): GeolocationPosition {
    return positions.reduce((prev, current) =>
      prev.coords.accuracy < current.coords.accuracy ? prev : current
    )
  }

  private handleError(error: any): Observable<never> {
    const errorMessages: Record<number, string> = {
      [error.PERMISSION_DENIED]: 'Permiso de geolocalización denegado',
      [error.POSITION_UNAVAILABLE]: 'Información de ubicación no disponible',
      [error.TIMEOUT]: 'Timeout al obtener la ubicación',
    }

    const defaultMessage = 'Error desconocido al obtener la ubicación'

    const errorMessage = errorMessages[error.code] ?? defaultMessage

    return throwError(() => new Error(errorMessage))
  }

  public getBestEffortPosition(maxWaitMs = 30000, desiredAccuracy = 20): Observable<GeolocationPosition> {
    const geolocation = navigator.geolocation;

    if (!geolocation) {
      return throwError(() => new Error('Geolocation no está soportada'));
    }

    return new Observable<GeolocationPosition>(observer => {
      let bestPosition: GeolocationPosition | null = null;

      const watchId = geolocation.watchPosition(
        (position) => {
          if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy) {
            bestPosition = position;
          }

          if (position.coords.accuracy <= desiredAccuracy) {
            observer.next(position)
            observer.complete()
            geolocation.clearWatch(watchId)
            clearTimeout(fallbackTimeout)
          }
        },
        (error) => {
          observer.error(error)
          clearTimeout(fallbackTimeout)
        },
        {
          enableHighAccuracy: true,
          timeout: maxWaitMs,
          maximumAge: 0
        }
      );

      const fallbackTimeout = setTimeout(() => {
        geolocation.clearWatch(watchId);
        if (bestPosition) {
          observer.next(bestPosition);
          observer.complete();
        } else {
          observer.error(new Error('No se pudo obtener una ubicación precisa'));
        }
      }, maxWaitMs);

      return () => {
        geolocation.clearWatch(watchId);
        clearTimeout(fallbackTimeout);
      };
    }).pipe(
      catchError((e) => this.handleError(e))
    );
  }
}